/**
 * Contains a few UI utilities for the Display Properties
 * section of case list/detail configuration.
 *
 * Depends on `add_ons` being available in initial page data
 */
import _ from "underscore";
import DOMPurify from "dompurify";
import toggles from "hqwebapp/js/toggles";
import initialPageData from "hqwebapp/js/initial_page_data";
import "select2/dist/js/select2.full.min";

var module = {};

module.fieldFormatWarningMessage = gettext("Must begin with a letter and contain only letters, numbers, '-', and '_'");

module.getFieldFormats = function () {
    var formats = [{
        value: "plain",
        label: gettext('Plain'),
    }, {
        value: "date",
        label: gettext('Date'),
    }, {
        value: "time-ago",
        label: gettext('Time Since or Until Date'),
    }, {
        value: "phone",
        label: gettext('Phone Number'),
    }, {
        value: "enum",
        label: gettext('ID Mapping'),
    }, {
        value: "late-flag",
        label: gettext('Late Flag'),
    }, {
        value: "invisible",
        label: gettext('Search Only'),
    }, {
        value: "address",
        label: gettext('Address'),
    }, {
        value: "distance",
        label: gettext('Distance from current location'),
    }, {
        value: "markdown",
        label: gettext('Markdown'),
    },{
        value: "geo-boundary",
        label: gettext('Geo Boundary (Mobile only)'),
    }, {
        value: "geo-boundary-color",
        label: gettext('Geo Boundary Color (Mobile only)'),
    }, {
        value: "geo-points",
        label: gettext('Geo Points (Mobile only)'),
    }, {
        value: "geo-points-colors",
        label: gettext('Geo Points Colors (Mobile only)'),
    }];

    if (toggles.toggleEnabled('CASE_LIST_MAP')) {
        formats.push({
            value: "address-popup",
            label: gettext('Address Popup (Web Apps only)'),
        });
    }

    if (toggles.toggleEnabled('CASE_LIST_CLICKABLE_ICON')) {
        formats.push({
            value: "clickable-icon",
            label: gettext('Clickable Icon (Web Apps only)'),
        });
    }

    if (toggles.toggleEnabled('MM_CASE_PROPERTIES')) {
        formats.push({
            value: "picture",
            label: gettext('Picture'),
        }, {
            value: "audio",
            label: gettext('Audio'),
        });
    }

    var addOns = initialPageData.get("add_ons");
    if (addOns.enum_image) {
        formats.push({
            value: "enum-image",
            label: gettext('Icon'),
        });
    }
    if (addOns.conditional_enum) {
        formats.push({
            value: "conditional-enum",
            label: gettext('Conditional ID Mapping'),
        });
    }
    if (addOns.calc_xpaths) {
        formats.push({
            value: "translatable-enum",
            label: gettext('Translatable Text'),
        });
    }

    if (toggles.toggleEnabled('VELLUM_CASE_MICRO_IMAGE')) {
        formats.push({
            value: "image",
            label: gettext('Image'),
        });
    }

    return formats;
};

// Dynamic format configuration and utilities
module.dynamicFormats = {
    // Configuration: Define format dependencies and display restrictions
    // Key = format name, Value = object with:
    //   - dependencies: array of required dependency formats (all must be present)
    //   - display: where format should be shown ('short' for case list, 'long' for case-detail, or 'both')
    COLUMN_FORMAT_DEPENDENCIES: {
        'geo-boundary': {
            dependencies: ['address'],
            display: 'short'
        },
        'geo-points': {
            dependencies: ['address'],
            display: 'short'
        },
        'geo-boundary-color': {
            dependencies: ['address', 'geo-boundary'],
            display: 'short'
        },
        'geo-points-colors': {
            dependencies: ['address', 'geo-points'],
            display: 'short'
        },
    },

    getFormatLabel: function (formatValue) {
        const format = module.getFieldFormats().find(f => f.value === formatValue);
        return format.label;
    },

    getDependencyAlertMessage: function (formatValue) {
        const formatLabel = this.getFormatLabel(formatValue);
        const dependencies = this.COLUMN_FORMAT_DEPENDENCIES[formatValue].dependencies;
        const dependencyLabels = dependencies.map(dep => this.getFormatLabel(dep)).join(', ');
        return interpolate(
            gettext('The "%(format)s" format requires columns with formats: %(dependencies)s.'),
            {
                format: formatLabel,
                dependencies: dependencyLabels
            },
            true
        );
    },
};

module.getFieldHtml = function (field) {
    var text = field || '';
    if (module.isAttachmentProperty(text)) {
        text = text.substring(text.indexOf(":") + 1);
    }
    var parts = text.split('/');
    // wrap all parts but the last in a label style
    for (var j = 0; j < parts.length - 1; j++) {
        parts[j] = ('<span class="label label-info">' +
            parts[j] + '</span>');
    }
    if (parts[j][0] === '#') {
        parts[j] = ('<span class="label label-info">' +
            module.toTitleCase(parts[j]) + '</span>');
    } else {
        parts[j] = ('<code style="display: inline-block;">' +
            parts[j] + '</code>');
    }
    return parts.join('<span style="color: #DDD;">/</span>');
};

module.isAttachmentProperty = function (value) {
    return value && value.indexOf("attachment:") === 0;
};

module.isValidPropertyName = function (name) {
    var word = '[a-zA-Z][\\w_-]*';
    var regex = new RegExp('^(' + word + ':)*(' + word + '\\/)*#?' + word + '$');
    return regex.test(name);
};

module.TIME_AGO = {
    year: 365.25,
    month: 365.25 / 12,
    week: 7,
    day: 1,
};

module.toTitleCase = function (str) {
    return (str
        .replace(/[_/-]/g, ' ')
        .replace(/#/g, '')
    ).replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
};

/**
 * Enable autocomplete on the given jquery element with the given autocomplete
 * options.
 * @param $elem
 * @param options: Array of strings.
 */
module.setUpAutocomplete = function ($elem, options) {
    if (!_.contains(options, $elem.value)) {
        options.unshift($elem.value);
    }
    $elem.$edit_view.select2({
        minimumInputLength: 0,
        width: '100%',
        tags: true,
        escapeMarkup: function (m) {
            return DOMPurify.sanitize(m);
        },
        templateResult: function (result) {
            var formatted = result.text;
            if (module.isAttachmentProperty(result.id)) {
                formatted = (
                    '<i class="fa fa-paperclip"></i> ' +
                    result.id.substring(result.id.indexOf(":") + 1)
                );
            }
            return DOMPurify.sanitize(formatted);
        },
    }).on('change', function () {
        $elem.val($elem.$edit_view.value);
        $elem.fire('change');
    });
    return $elem;
};

export default module;
